import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Menu,
  Pagination,
} from '@mui/material';
import { createPortal } from 'react-dom';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../../services';
import { usePublicLinkAccess } from '../../../context/PublicLinkAccessContext';
import { publicLinksService } from '../../../services/public-links/public-links.service';
import UniversalSearchBar, { SearchQuery } from '../../content/UniversalSearchBar';
import FilterSidebar, { ActiveFilters } from '../../content/FilterSidebar';
import { ContentTile } from '../../content/ContentTile';
import { ContentRow } from '../../content/ContentRow';
import { ContentLink } from '../../content/ContentLink';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getFileTypeConfig } from '../../../utils/fileIconUtils';
import { useOrganization } from '../../../context/OrganizationContext';
import { useRBAC } from '../../../context/RBACContext';

// Define component props based on the schema - matching ViewContentPick structure
interface ViewContentQueryProps {
  // Saved query configuration
  searchQuery?: string;
  filters?: {
    mimeTypes?: string[];
    tagGroupFilters?: Record<string, string[]>;
    systemTags?: string[];
    personalTags?: string[];
    tags?: string[];
    authors?: string[];
    organizations?: string[];
    teams?: string[];
    dateRange?: {
      start?: Date | null;
      end?: Date | null;
    };
    rating?: {
      min?: number;
      max?: number;
    };
    bookmarkedOnly?: boolean;
    personalTagsOnly?: boolean;
    systemTagsOnly?: boolean;
    funnelStages?: string[];
    personas?: string[];
  };
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  
  // Callback for saving query
  onQueryChange?: (query: string, filters: any, sortBy: string, sortOrder: string) => void;
  
  // Display configuration - identical to ViewContentPick
  displayMode?: 'grid' | 'list' | 'tile' | 'minimal';
  bannerStyle?: 'thumbnails' | 'illustrations';
  maxItems?: number;
  showMetadata?: boolean;
  showIcon?: boolean;
  showActions?: boolean;
  showRating?: boolean;
  showDescription?: boolean;
  enableQueryBuilder?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  borderRadius?: number;
  padding?: number;
  margin?: number;
  gap?: number;
  columns?: number;
  itemBackgroundColor?: string;
  itemBorderColor?: string;
  itemBorderWidth?: number;
  itemBorderRadius?: number;
  itemPadding?: number;
  itemElevation?: number;
  width?: string;
  height?: string;
  containerHeight?: string; // Height of the scrollable container
  enableScroll?: boolean; // Enable scrolling when content exceeds containerHeight
  isEditing?: boolean;

  // Pagination configuration
  pageSize?: number; // authored page size seeded from block config

  // Auto-refresh configuration
  autoRefresh?: boolean;
  refreshInterval?: number; // in seconds
  componentId?: string; // pageDoc block id; used in public mode for self-resolution
}

// Helper function to format file size
const formatFileSize = (fileSize?: string | number): string => {
  // Handle string input by parsing to number
  if (typeof fileSize === 'string') {
    const parsed = parseInt(fileSize, 10);
    if (!isNaN(parsed)) {
      fileSize = parsed;
    } else {
      return fileSize; // Return as-is if not parseable
    }
  }

  if (typeof fileSize === 'number') {
    const sizes = ['B', 'K', 'M', 'G'];
    if (fileSize === 0) return '0 B';
    const i = Math.floor(Math.log(fileSize) / Math.log(1024));
    const value = fileSize / Math.pow(1024, i);
    return value.toFixed(1) + sizes[i];
  }

  return 'Unknown size';
};

// Helper function to safely format dates
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '';
  }
};

// Error boundary wrapper for safe rendering
class ViewContentQueryErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: '' };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error: error?.message || 'An error occurred' };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('ViewContentQuery error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          Failed to load content query: {this.state.error}
        </Alert>
      );
    }

    return this.props.children;
  }
}

const ViewContentQueryInner: React.FC<ViewContentQueryProps> = ({
  searchQuery: initialSearchQuery = '',
  filters: initialFilters = {},
  sortBy: initialSortBy = 'updatedAt',
  sortOrder: initialSortOrder = 'desc',
  onQueryChange,
  displayMode = 'grid',
  bannerStyle = 'illustrations',
  maxItems = 12,
  showIcon = true,
  showDescription = true,
  showMetadata = true,
  showRating = true,
  showActions = true,
  enableQueryBuilder = true,
  columns = 3,
  gap = 2,
  itemBackgroundColor,
  itemBorderColor,
  itemBorderWidth,
  itemBorderRadius,
  itemPadding,
  itemElevation = 1,
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'none',
  borderRadius,
  padding,
  margin,
  width = '100%',
  height,
  containerHeight,
  enableScroll = false,
  isEditing = false,
  pageSize: authoredPageSize,
  autoRefresh = false,
  refreshInterval = 60, // Default 60 seconds
  componentId,
}) => {
  const navigate = useNavigate();
  const { currentOrganizationId, currentTeamId } = useOrganization();
  const { aggregatedPermissions } = useRBAC();

  // Public-mode detection
  const publicLinkCreds = usePublicLinkAccess();
  const isPublicMode = !!publicLinkCreds;

  // Pagination page-size state
  const DEFAULT_PAGE_SIZE = 25;
  const PUBLIC_PAGE_SIZE_OPTIONS = [25, 50];
  const IN_APP_PAGE_SIZE_OPTIONS = [25, 50, 100];
  const [pageSize, setPageSize] = useState<number>(authoredPageSize || DEFAULT_PAGE_SIZE);
  const [mainCurrentPage, setMainCurrentPage] = useState<number>(1);
  const [mainTotalResults, setMainTotalResults] = useState<number>(0);

  // If the author edits maxItems (or pageSize) down below the current page,
  // snap back to page 1 so the user isn't stuck on a page that no longer
  // exists under the new cap.
  useEffect(() => {
    const lastValidPage = Math.max(1, Math.ceil(Math.min(mainTotalResults, maxItems) / pageSize));
    if (mainCurrentPage > lastValidPage) {
      setMainCurrentPage(1);
    }
  }, [maxItems, pageSize, mainTotalResults, mainCurrentPage]);

  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Query builder dialog state
  const [dialogSearchText, setDialogSearchText] = useState(initialSearchQuery);
  const [dialogFilters, setDialogFilters] = useState<ActiveFilters>({
    mimeTypes: initialFilters.mimeTypes || [],
    tagGroupFilters: initialFilters.tagGroupFilters || {},
    systemTags: initialFilters.systemTags || [],
    personalTags: initialFilters.personalTags || [],
    tags: initialFilters.tags || [],
    authors: initialFilters.authors || [],
    organizations: initialFilters.organizations || [],
    teams: initialFilters.teams || [],
    dateRange: {
      start: initialFilters.dateRange?.start || null,
      end: initialFilters.dateRange?.end || null,
    },
    rating: {
      min: initialFilters.rating?.min || 0,
      max: initialFilters.rating?.max || 5,
    },
    bookmarkedOnly: initialFilters.bookmarkedOnly || false,
    statuses: [],
    personalTagsOnly: initialFilters.personalTagsOnly || false,
    systemTagsOnly: initialFilters.systemTagsOnly || false,
    funnelStages: initialFilters.funnelStages || [],
    personas: initialFilters.personas || [],
  });
  const [dialogSortBy, setDialogSortBy] = useState(initialSortBy);
  const [dialogSortOrder, setDialogSortOrder] = useState(initialSortOrder);
  
  // Dialog state for search results preview
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Sort menu state
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortMenuPosition, setSortMenuPosition] = useState<{top: number, left: number} | null>(null);

  // Execute the saved query to fetch content (uses saved values, not dialog values)
  const executeQuery = useCallback(async (customQuery?: {
    searchQuery?: string;
    filters?: any;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    pageSizeOverride?: number;
  }) => {
    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const activePage = customQuery?.page ?? mainCurrentPage;
      const activePageSize = customQuery?.pageSizeOverride ?? pageSize;

      // --- Public-mode branch ---
      if (isPublicMode && publicLinkCreds && componentId) {
        // Branch on credential shape. Top-level supports either the entered
        // password OR a share-session token (when React state has lost the
        // password, e.g., back-nav restoring the page). Nested uses an
        // access token whose existence is proof of clearance.
        const authPart =
          'accessToken' in publicLinkCreds && publicLinkCreds.accessToken
            ? { accessToken: publicLinkCreds.accessToken }
            : {
                token: publicLinkCreds.token,
                password: publicLinkCreds.password,
                shareToken: publicLinkCreds.shareToken,
              };
        const res = await publicLinksService.resolveComponent(
          publicLinkCreds.linkId,
          componentId,
          {
            ...authPart,
            page: activePage,
            pageSize: activePageSize,
          }
        );
        setContent(res.items.map((it: any) => ({
          id: it.id,
          title: it.title,
          description: it.description || '',
          mimeType: it.mimeType || '',
          thumbnailUrl: it.thumbnailUrl || '',
          kind: it.kind,
          accessToken: it.accessToken,
          // Tile-rendering metadata
          fileSize: it.fileSize,
          fileSizeBytes: it.fileSizeBytes,
          fileName: it.fileName,
          createdAt: it.createdAt,
          status: it.status,
          viewCount: it.viewCount ?? 0,
          downloadCount: it.downloadCount ?? 0,
          averageRating: it.averageRating ?? 0,
          tagIds: it.tagIds ?? [],
          category: it.category,
          authorName: it.authorName,
        })));
        setMainTotalResults(res.pagination?.totalCount ?? res.items.length);
        if (res.items.length === 0) {
          setWarning('No content available');
        }
        return;
      }

      // --- In-app (authenticated) branch ---

      // Ensure we have organization and team context
      if (!currentOrganizationId || !currentTeamId) {
        console.warn('No organization or team context available for query');
        setContent([]);
        return;
      }

      // Use custom values if provided, otherwise use saved values
      const queryToUse = customQuery?.searchQuery ?? initialSearchQuery;
      const filtersToUse = customQuery?.filters ?? initialFilters;
      const sortByToUse = customQuery?.sortBy ?? initialSortBy;
      const sortOrderToUse = customQuery?.sortOrder ?? initialSortOrder;

      const searchParams: any = {
        query: queryToUse,
        organization_id: currentOrganizationId,
        team_id: currentTeamId,
        sort_by: sortByToUse,
        sort_order: sortOrderToUse,
        page: activePage,
        limit: activePageSize,
        fuzzy_search: true,
        include_archived: false,
        user_permissions: aggregatedPermissions, // Pass user permissions for RBAC filtering
      };

      // Add ALL filters using correct API parameter names
      if (filtersToUse.mimeTypes?.length) {
        searchParams.mime_types = filtersToUse.mimeTypes;
      }

      if (filtersToUse.authors?.length) {
        searchParams.authors = filtersToUse.authors;
      }

      if (filtersToUse.systemTags?.length) {
        searchParams.system_tags = filtersToUse.systemTags;
      }

      if (filtersToUse.personalTags?.length) {
        searchParams.personal_tags = filtersToUse.personalTags;
      }

      if (filtersToUse.tagGroupFilters && Object.keys(filtersToUse.tagGroupFilters).length > 0) {
        searchParams.tag_group_filters = filtersToUse.tagGroupFilters;
      }

      if (filtersToUse.dateRange?.start) {
        searchParams.created_after = filtersToUse.dateRange.start.toISOString();
      }
      if (filtersToUse.dateRange?.end) {
        searchParams.created_before = filtersToUse.dateRange.end.toISOString();
      }

      if (filtersToUse.rating) {
        if (filtersToUse.rating.min > 1) {
          searchParams.min_rating = filtersToUse.rating.min;
        }
        if (filtersToUse.rating.max < 5) {
          searchParams.max_rating = filtersToUse.rating.max;
        }
      }

      if (filtersToUse.bookmarkedOnly) {
        searchParams.bookmarked_only = true;
      }
      if (filtersToUse.personalTagsOnly) {
        searchParams.personal_tags_only = true;
      }
      if (filtersToUse.systemTagsOnly) {
        searchParams.system_tags_only = true;
      }

      console.log('Executing saved query with params:', searchParams);
      console.log('User permissions for RBAC filtering:', aggregatedPermissions);

      const response = await searchService.searchContent(searchParams);
      const results = response?.data || [];
      const pagination = response?.metadata?.pagination;

      setContent(results);
      setMainTotalResults(pagination?.totalCount ?? results.length);

      if (results.length === 0) {
        setWarning('No content matches your query criteria');
      }
    } catch (err: any) {
      console.error('Query execution error:', err);
      const errorMessage = err?.message || 'Failed to execute query';
      setError(errorMessage);
      setContent([]);
      setMainTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [isPublicMode, publicLinkCreds, componentId, mainCurrentPage, pageSize,
      currentOrganizationId, currentTeamId, initialSearchQuery, initialFilters,
      initialSortBy, initialSortOrder, aggregatedPermissions]);

  // Search for the dialog preview (uses dialog state)
  const performDialogSearch = async (page: number = 1, append: boolean = false) => {
    if (!append) {
      setSearchLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      // Ensure we have organization and team context
      if (!currentOrganizationId || !currentTeamId) {
        console.warn('No organization or team context available for search');
        if (!append) {
          setSearchResults([]);
          setTotalResults(0);
        }
        return;
      }

      const searchParams: any = {
        query: dialogSearchText,
        organization_id: currentOrganizationId,
        team_id: currentTeamId,
        sort_by: dialogSortBy,
        sort_order: dialogSortOrder,
        page: page,
        limit: 30,
        fuzzy_search: true,
        include_archived: false,
        user_permissions: aggregatedPermissions, // Pass user permissions for RBAC filtering
      };

      // Add ALL filters from dialogFilters
      if (dialogFilters.mimeTypes?.length) {
        searchParams.mime_types = dialogFilters.mimeTypes;
      }
      
      if (dialogFilters.authors?.length) {
        searchParams.authors = dialogFilters.authors;
      }
      
      if (dialogFilters.systemTags?.length) {
        searchParams.system_tags = dialogFilters.systemTags;
      }
      
      if (dialogFilters.personalTags?.length) {
        searchParams.personal_tags = dialogFilters.personalTags;
      }
      
      if (dialogFilters.tagGroupFilters && Object.keys(dialogFilters.tagGroupFilters).length > 0) {
        searchParams.tag_group_filters = dialogFilters.tagGroupFilters;
      }
      
      if (dialogFilters.dateRange?.start) {
        searchParams.created_after = dialogFilters.dateRange.start.toISOString();
      }
      if (dialogFilters.dateRange?.end) {
        searchParams.created_before = dialogFilters.dateRange.end.toISOString();
      }
      
      if (dialogFilters.rating) {
        if (dialogFilters.rating.min > 1) {
          searchParams.min_rating = dialogFilters.rating.min;
        }
        if (dialogFilters.rating.max < 5) {
          searchParams.max_rating = dialogFilters.rating.max;
        }
      }
      
      if (dialogFilters.bookmarkedOnly) {
        searchParams.bookmarked_only = true;
      }
      if (dialogFilters.personalTagsOnly) {
        searchParams.personal_tags_only = true;
      }
      if (dialogFilters.systemTagsOnly) {
        searchParams.system_tags_only = true;
      }

      console.log('Dialog search params:', searchParams);

      const response = await searchService.searchContent(searchParams);
      const results = response?.data || [];
      const pagination = response?.metadata?.pagination;
      
      if (append) {
        setSearchResults(prev => [...prev, ...results]);
      } else {
        setSearchResults(results);
      }
      
      setTotalResults(pagination?.totalCount || results.length);
      setCurrentPage(pagination?.currentPage || 1);
      setHasMorePages(pagination?.hasNextPage || false);
    } catch (err: any) {
      console.warn('Search error:', err?.message || err);
      if (!append) {
        setSearchResults([]);
        setTotalResults(0);
      }
    } finally {
      setSearchLoading(false);
      setLoadingMore(false);
    }
  };

  // Execute query on mount and when dependencies change
  useEffect(() => {
    if (!isEditing) {
      executeQuery().catch(err => {
        console.error('Error executing query:', err);
        setLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId, currentTeamId, isEditing, mainCurrentPage, pageSize]);

  // Set up auto-refresh if enabled
  useEffect(() => {
    if (autoRefresh && !isEditing && refreshInterval > 0) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      
      // Set up new interval
      refreshIntervalRef.current = setInterval(() => {
        executeQuery().catch(err => {
          console.error('Error in auto-refresh:', err);
        });
      }, refreshInterval * 1000);
      
      // Cleanup on unmount or when auto-refresh changes
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, refreshInterval, isEditing]);

  // Handle saving the query configuration
  const handleSaveQuery = () => {
    if (onQueryChange) {
      onQueryChange(dialogSearchText, dialogFilters, dialogSortBy, dialogSortOrder);
    }
    setDialogOpen(false);
    // Execute the new query immediately with the dialog values
    executeQuery({
      searchQuery: dialogSearchText,
      filters: dialogFilters,
      sortBy: dialogSortBy,
      sortOrder: dialogSortOrder,
    });
  };

  // Handle opening the dialog
  const handleOpenDialog = () => {
    // Reset dialog state to current saved values
    setDialogSearchText(initialSearchQuery);
    setDialogFilters({
      mimeTypes: initialFilters.mimeTypes || [],
      tagGroupFilters: initialFilters.tagGroupFilters || {},
      systemTags: initialFilters.systemTags || [],
      personalTags: initialFilters.personalTags || [],
      tags: initialFilters.tags || [],
      authors: initialFilters.authors || [],
      organizations: initialFilters.organizations || [],
      teams: initialFilters.teams || [],
      dateRange: {
        start: initialFilters.dateRange?.start || null,
        end: initialFilters.dateRange?.end || null,
      },
      rating: {
        min: initialFilters.rating?.min || 0,
        max: initialFilters.rating?.max || 5,
      },
      bookmarkedOnly: initialFilters.bookmarkedOnly || false,
      statuses: [],
      personalTagsOnly: initialFilters.personalTagsOnly || false,
      systemTagsOnly: initialFilters.systemTagsOnly || false,
      funnelStages: initialFilters.funnelStages || [],
      personas: initialFilters.personas || [],
    });
    setDialogSortBy(initialSortBy);
    setDialogSortOrder(initialSortOrder);
    setSearchResults([]);
    setCurrentPage(1);
    setHasMorePages(false);
    setDialogOpen(true);
  };

  // Trigger search when dialog query changes
  const filterKey = JSON.stringify(dialogFilters);
  useEffect(() => {
    if (dialogOpen) {
      console.log('Triggering search with filters:', dialogFilters, 'and query:', dialogSearchText);
      // Reset pagination and perform new search
      setCurrentPage(1);
      setHasMorePages(false);
      performDialogSearch(1, false).catch((error) => {
        console.warn('Error performing search:', error);
      });
    }
  }, [dialogSearchText, dialogSortBy, dialogSortOrder, dialogOpen, filterKey]);

  // Handle scroll for infinite loading in dialog
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    
    // Load more when user scrolls to 80% of the content
    if (scrollPercentage > 0.8 && hasMorePages && !loadingMore && !searchLoading) {
      performDialogSearch(currentPage + 1, true);
    }
  };

  const handleContentClick = (contentId: string) => {
    try {
      if (!isEditing) {
        // Public-mode branch: use signed access token
        if (isPublicMode) {
          const clickedContent = content.find(c => c.id === contentId);
          if (clickedContent?.accessToken) {
            navigate(`/p/content?access=${encodeURIComponent(clickedContent.accessToken)}`);
          }
          return;
        }

        // In-app branch
        const clickedContent = content.find(c => c.id === contentId);
        if (clickedContent) {
          if (clickedContent.status === 0 || clickedContent.status === 'draft') {
            console.warn('Cannot view draft content without file');
            setWarning('This content is in draft status and cannot be viewed');
            return;
          }
        }
        navigate(`/content/${contentId}`);
      }
    } catch (error) {
      console.error('Error navigating to content:', error);
    }
  };

  const renderContentItem = (rawItem: any, _index: number) => {
    const item = bannerStyle === 'illustrations'
      ? { ...rawItem, thumbnailUrl: '', thumbnail_url: '' }
      : rawItem;
    const isDraft = item.status === 0 || item.status === 1 || item.status === 'draft';
    // For search results, fileUrl may be empty but content is still viewable if private or public
    // So we check: not draft AND (has fileUrl OR status is private/public)
    const isViewable = !isDraft && (!!item.fileUrl || item.status === 'private' || item.status === 'public' || item.status === 2 || item.status === 3);

    const showPrimitive = {
      thumbnail: showIcon,
      description: showDescription,
      meta: showMetadata,
      stats: showRating,
      actions: showActions && !isEditing,
    };
    const handlePrimitiveClick = !isEditing && isViewable
      ? () => handleContentClick(item.id)
      : undefined;

    // Minimal View
    if (displayMode === 'minimal') {
      return (
        <ContentLink
          content={item}
          onClick={handlePrimitiveClick}
          disabled={!isViewable}
        />
      );
    }

    // List View
    if (displayMode === 'list') {
      return (
        <ContentRow
          content={item}
          show={showPrimitive}
          onClick={handlePrimitiveClick}
        />
      );
    }

    // Tile View
    if (displayMode === 'tile') {
      return (
        <ContentTile
          content={item}
          variant="compact"
          show={showPrimitive}
          onClick={handlePrimitiveClick}
        />
      );
    }

    // Grid View (default)
    return (
      <ContentTile
        content={item}
        variant="grid"
        show={showPrimitive}
        onClick={handlePrimitiveClick}
      />
    );
  };

  // Effective total (what the user sees in pagination) is the server total
  // capped by the author's maxItems config. Beyond maxItems, even if more
  // results match the query, the component does not surface them.
  const effectiveTotal = Math.min(mainTotalResults, maxItems);
  // Items visible on the current page: trimmed if the page boundary would
  // overrun the maxItems cap (e.g., maxItems=60, pageSize=25, page=3 starts
  // at offset 50 → only 10 items shown on this final page).
  const pageStart = (mainCurrentPage - 1) * pageSize;
  const remainingUnderCap = Math.max(0, maxItems - pageStart);
  const visibleContent = content.slice(0, remainingUnderCap);

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => executeQuery()}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }

    if (visibleContent.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            No Content Found
          </Typography>
          {isEditing && enableQueryBuilder ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure your search query to display content
              </Typography>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleOpenDialog}
              >
                Configure Query
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {warning || 'No content matches the current query criteria'}
            </Typography>
          )}
        </Paper>
      );
    }

    if (displayMode === 'list') {
      return (
        <Box>
          {visibleContent.map((item, index) => (
            <React.Fragment key={item.id}>
              {renderContentItem(item, index)}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    if (displayMode === 'minimal') {
      return (
        <Paper
          variant="outlined"
          sx={{
            backgroundColor: backgroundColor || 'background.paper',
            borderColor: borderColor,
            borderWidth: borderWidth,
            borderStyle: borderStyle !== 'none' ? borderStyle : undefined,
            borderRadius: `${borderRadius}px`,
            overflow: 'hidden',
          }}
        >
          {visibleContent.map((item, index) => (
            <React.Fragment key={item.id}>
              {renderContentItem(item, index)}
            </React.Fragment>
          ))}
        </Paper>
      );
    }

    // Use native CSS Grid (not MUI's flex-based <Grid>): it lets us pin
    // every column to an exact 1/N fraction of the container with
    // `minmax(0, 1fr)`, regardless of child content widths. The legacy
    // MUI Grid path was content-sensitive — pages whose tiles had certain
    // intrinsic widths overflowed past the container.
    //
    // The MUI spacing prop is in 8px units, so we translate `gap` to px
    // for the native CSS gap property.
    const gapPx = `${(gap ?? 1) * 8}px`;

    return (
      <Box
        sx={{
          display: 'grid',
          // Single-column on xs, two on sm, then `columns` on md+. Matches
          // the previous MUI Grid breakpoints (xs=12, sm=6, md=gridColWidth).
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: `repeat(${columns}, minmax(0, 1fr))`,
          },
          gap: gapPx,
          width: '100%',
          minWidth: 0,
        }}
      >
        {visibleContent.map((item, index) => (
          <Box key={item.id} sx={{ minWidth: 0 }}>
            {renderContentItem(item, index)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor,
          borderColor,
          borderWidth,
          borderStyle: borderWidth > 0 ? borderStyle : undefined,
          borderRadius,
          padding,
          margin,
          width: width || '100%',
          height: containerHeight || height,
          overflow: enableScroll ? 'auto' : 'visible',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Show the query builder button when in edit mode */}
        {isEditing ? (
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Box>
              <Typography variant="subtitle1">
                Content Query
              </Typography>
              {initialSearchQuery || initialFilters?.tags?.length || initialFilters?.mimeTypes?.length ? (
                <Typography variant="caption" color="text.secondary">
                  Query: "{initialSearchQuery || 'All content'}" | Sort: {initialSortBy} ({initialSortOrder})
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No query configured - showing all content
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleOpenDialog}
            >
              Configure Query
            </Button>
          </Box>
        ) : null}
        {warning && content.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2, flexShrink: 0 }}
            onClose={() => setWarning(null)}
          >
            {warning}
          </Alert>
        )}
        <Box sx={{
          flex: enableScroll ? 1 : 'none',
          overflow: enableScroll ? 'auto' : 'visible',
          minHeight: 0,
        }}>
          {renderContent()}
        </Box>

        {/* Pagination controls — shown whenever the *effective* total
            (server total capped by the author's maxItems) exceeds page size. */}
        {effectiveTotal > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
            <Pagination
              count={Math.ceil(effectiveTotal / pageSize)}
              page={mainCurrentPage}
              onChange={(_: React.ChangeEvent<unknown>, value: number) => setMainCurrentPage(value)}
              color="primary"
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Page size</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Page size"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setMainCurrentPage(1);
                }}
              >
                {(isPublicMode ? PUBLIC_PAGE_SIZE_OPTIONS : IN_APP_PAGE_SIZE_OPTIONS).map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Query Builder Dialog - Similar to ViewContentPick selector */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ 
          zIndex: 10000,
          '& .MuiDialog-paper': {
            height: '85vh',
            maxHeight: '900px'
          },
          '& .MuiSelect-root': {
            zIndex: 10001
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Configure Search Query</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {/* Sort Options as buttons with menus */}
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowDownIcon />}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  
                  const buttonRect = event.currentTarget.getBoundingClientRect();
                  const position = {
                    top: buttonRect.bottom + window.scrollY + 4,
                    left: buttonRect.left + window.scrollX
                  };
                  
                  setSortMenuAnchor(event.currentTarget);
                  setSortMenuPosition(position);
                }}
                sx={{ 
                  minWidth: 120,
                  backgroundColor: sortMenuAnchor ? 'primary.light' : 'transparent'
                }}
              >
                Sort: {dialogSortBy === 'updatedAt' ? 'Updated' : 
                       dialogSortBy === 'createdAt' ? 'Created' : 
                       dialogSortBy === 'title' ? 'Title' : 
                       dialogSortBy === 'rating' ? 'Rating' : 
                       dialogSortBy === 'views' ? 'Views' : 'Downloads'}
              </Button>
              <Button
                variant="outlined" 
                size="small"
                endIcon={dialogSortOrder === 'desc' ? <ArrowDownIcon /> : <ArrowUpIcon />}
                onClick={() => {
                  setDialogSortOrder(dialogSortOrder === 'desc' ? 'asc' : 'desc');
                }}
                sx={{ minWidth: 80 }}
              >
                {dialogSortOrder === 'desc' ? 'Desc' : 'Asc'}
              </Button>
              <Chip
                label={`${totalResults} results`}
                color="primary"
                variant={totalResults > 0 ? 'filled' : 'outlined'}
              />
              <IconButton onClick={() => setDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box display="flex" height="100%">
            {/* Left Panel - Filters (Collapsible) */}
            <Box 
              sx={{ 
                width: showFilters ? 280 : 0,
                flexShrink: 0,
                borderRight: showFilters ? 1 : 0,
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'width 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {showFilters && (
                <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
                  <FilterSidebar
                    activeFilters={dialogFilters}
                    onChange={setDialogFilters}
                  />
                </Box>
              )}
            </Box>

            {/* Right Panel - Search and Results */}
            <Box flex={1} display="flex" flexDirection="column" p={2}>
              {/* Search Bar with Filter Toggle */}
              <Box mb={1} display="flex" gap={1}>
                <IconButton 
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    border: 1,
                    borderColor: showFilters ? 'primary.main' : 'grey.300',
                    backgroundColor: showFilters ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: showFilters ? 'primary.light' : 'grey.100',
                    }
                  }}
                  size="small"
                >
                  <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                    <FilterListIcon fontSize="small" color={showFilters ? "primary" : "inherit"} />
                  </Tooltip>
                </IconButton>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search for content..."
                  value={dialogSearchText}
                  onChange={(e) => setDialogSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Box>

              {/* Document Count */}
              {totalResults > 0 && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {totalResults} document{totalResults !== 1 ? 's' : ''} found
                    {searchResults.length < totalResults && ` (showing ${searchResults.length})`}
                  </Typography>
                </Box>
              )}

              {/* Active Filters Display */}
              {!showFilters && (dialogFilters.tags?.length > 0 || 
                dialogFilters.authors?.length > 0 || 
                dialogFilters.organizations?.length > 0 ||
                dialogFilters.mimeTypes?.length > 0) && (
                <Box mb={2} display="flex" flexWrap="wrap" gap={0.5}>
                  {dialogFilters.tags?.map(tag => (
                    <Chip 
                      key={tag} 
                      label={tag} 
                      size="small" 
                      onDelete={() => setDialogFilters({
                        ...dialogFilters,
                        tags: dialogFilters.tags?.filter(t => t !== tag)
                      })}
                    />
                  ))}
                  {dialogFilters.mimeTypes?.map(type => (
                    <Chip 
                      key={type} 
                      label={type} 
                      size="small" 
                      onDelete={() => setDialogFilters({
                        ...dialogFilters,
                        mimeTypes: dialogFilters.mimeTypes?.filter(t => t !== type)
                      })}
                    />
                  ))}
                  {(dialogFilters.tags?.length > 0 || dialogFilters.mimeTypes?.length > 0) && (
                    <Button 
                      size="small" 
                      onClick={() => setDialogFilters({
                        ...dialogFilters,
                        tags: [],
                        mimeTypes: [],
                        authors: [],
                        organizations: []
                      })}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              )}

              {/* Search Results Preview */}
              <Box sx={{ flex: 1, overflow: 'auto' }} onScroll={handleScroll}>
                {searchLoading && searchResults.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : searchResults.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={4}>
                    <Typography variant="body1" color="text.secondary">
                      {dialogSearchText || dialogFilters.tags?.length > 0 || dialogFilters.mimeTypes?.length > 0
                        ? 'No results found. Try adjusting your search criteria.'
                        : 'Enter search terms or select filters to find content'}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {searchResults.map((item) => {
                      const typeConfig = getFileTypeConfig(item.mimeType, item.title);
                      const fileSize = item.fileSize ? formatFileSize(item.fileSize) : null;
                      
                      return (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {/* File icon */}
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: typeConfig.bgColor,
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          >
                            {React.cloneElement(typeConfig.icon, { 
                              sx: { 
                                fontSize: '1.5rem',
                                color: typeConfig.color 
                              }
                            })}
                          </Box>

                          {/* Content info */}
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {item.title || 'Untitled'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {item.authorName || 'Unknown'}
                              </Typography>
                              {fileSize && (
                                <>
                                  <Typography variant="caption" color="text.secondary">•</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {fileSize}
                                  </Typography>
                                </>
                              )}
                              <Typography variant="caption" color="text.secondary">•</Typography>
                              <Chip
                                size="small"
                                label={typeConfig.label}
                                sx={{
                                  height: 18,
                                  fontSize: '0.7rem',
                                  backgroundColor: typeConfig.color,
                                  color: 'white',
                                  fontWeight: 500,
                                }}
                              />
                            </Stack>
                          </Box>

                          {/* Date */}
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatDate(item.createdAt)}
                          </Typography>
                        </Paper>
                      );
                    })}
                    
                    {/* Loading indicator for infinite scroll */}
                    {loadingMore && (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    
                    {/* End of results message */}
                    {!hasMorePages && searchResults.length > 0 && searchResults.length === totalResults && (
                      <Box display="flex" justifyContent="center" p={2}>
                        <Typography variant="caption" color="text.secondary">
                          All {totalResults} results loaded
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveQuery}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save Query
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Render Menu as Portal with absolute positioning */}
      {typeof document !== 'undefined' && sortMenuPosition && createPortal(
        <>
          {/* Backdrop to close menu when clicking outside */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99998,
            }}
            onClick={() => {
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              top: sortMenuPosition.top,
              left: sortMenuPosition.left,
              zIndex: 99999,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              boxShadow: 3,
              minWidth: 120
            }}
          >
          <MenuItem 
            onClick={() => {
              setDialogSortBy('updatedAt');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'updatedAt'}
          >
            Updated
          </MenuItem>
          <MenuItem 
            onClick={() => {
              setDialogSortBy('createdAt');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'createdAt'}
          >
            Created
          </MenuItem>
          <MenuItem 
            onClick={() => {
              setDialogSortBy('title');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'title'}
          >
            Title
          </MenuItem>
          <MenuItem 
            onClick={() => {
              setDialogSortBy('rating');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'rating'}
          >
            Rating
          </MenuItem>
          <MenuItem 
            onClick={() => {
              setDialogSortBy('views');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'views'}
          >
            Views
          </MenuItem>
          <MenuItem 
            onClick={() => {
              setDialogSortBy('downloads');
              setSortMenuAnchor(null);
              setSortMenuPosition(null);
            }}
            selected={dialogSortBy === 'downloads'}
          >
            Downloads
          </MenuItem>
        </Box>
        </>,
        document.body
      )}
    </>
  );
};

// Wrap with error boundary for safety
const ViewContentQuery: React.FC<ViewContentQueryProps> = (props) => {
  return (
    <ViewContentQueryErrorBoundary>
      <ViewContentQueryInner {...props} />
    </ViewContentQueryErrorBoundary>
  );
};

export default ViewContentQuery;